import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Plus, Clock, CheckCircle2, AlertCircle, Send, AtSign, X, MessageSquare, Trash2, Mic, MicOff, Loader2, Search, LayoutGrid, List, ArrowUpDown, ChevronUp, ChevronDown, Filter, History } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

/* ─── MentionInput ─── */
const MentionInput = ({ value, onChange, onSubmit, users, placeholder }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) { onSubmit(value, selectedMentions); setSelectedMentions([]); }
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    const cursor = e.target.selectionStart;
    const textBefore = text.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) { setMentionStart(cursor - atMatch[1].length); setMentionQuery(atMatch[1].toLowerCase()); setShowSuggestions(true); }
    else { setShowSuggestions(false); setMentionQuery(''); }
  };

  const insertMention = (user) => {
    const fullName = `${user.nome} ${user.cognome}`;
    const before = value.slice(0, mentionStart - 1);
    const after = value.slice(mentionStart + mentionQuery.length);
    onChange(`${before}@${fullName} ${after}`);
    setShowSuggestions(false);
    setSelectedMentions([...selectedMentions, user.id]);
    inputRef.current?.focus();
  };

  const filteredUsers = users.filter(u => `${u.nome} ${u.cognome}`.toLowerCase().includes(mentionQuery));

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input ref={inputRef} value={value} onChange={handleChange} onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Scrivi una nota... usa @ per menzionare'} className="pr-10 h-10 text-sm" data-testid="note-input" />
          <AtSign size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
        <Button type="button" size="icon" className="h-10 w-10 bg-blue-600 hover:bg-blue-700 flex-shrink-0"
          onClick={() => { if (value.trim()) { onSubmit(value, selectedMentions); setSelectedMentions([]); } }} data-testid="note-submit">
          <Send size={16} />
        </Button>
      </div>
      {showSuggestions && filteredUsers.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden" data-testid="mention-suggestions">
          <p className="text-[10px] text-slate-400 px-3 pt-2 pb-1 uppercase font-semibold">Menziona</p>
          {filteredUsers.slice(0, 5).map(u => (
            <button key={u.id} onClick={() => insertMention(u)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left transition-colors" data-testid={`mention-${u.id}`}>
              <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-slate-100">{u.nome?.[0]}{u.cognome?.[0]}</AvatarFallback></Avatar>
              <span className="text-sm text-slate-700">{u.nome} {u.cognome}</span>
              <Badge variant="secondary" className="ml-auto text-[9px]">{u.ruolo === 'super_admin' ? 'Admin' : 'Dip.'}</Badge>
            </button>
          ))}
        </div>
      )}
      {selectedMentions.length > 0 && (
        <div className="flex gap-1 mt-1.5 flex-wrap">
          {selectedMentions.map(id => {
            const u = users.find(x => x.id === id);
            return u ? (
              <Badge key={id} variant="secondary" className="text-[10px] bg-blue-50 text-blue-600 gap-1">
                @{u.nome} {u.cognome}
                <button onClick={() => setSelectedMentions(selectedMentions.filter(x => x !== id))}><X size={10} /></button>
              </Badge>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

/* ─── NoteItem ─── */
const NoteItem = ({ note }) => {
  const renderText = (text) => {
    const parts = text.split(/(@\w+\s\w+)/g);
    return parts.map((part, i) => part.startsWith('@')
      ? <span key={i} className="text-blue-600 font-semibold bg-blue-50 px-0.5 rounded">{part}</span>
      : <span key={i}>{part}</span>);
  };
  const timeStr = note.created_at ? format(new Date(note.created_at), 'dd MMM HH:mm', { locale: it }) : '';

  return (
    <div className="flex gap-3 py-3" data-testid={`note-${note.id}`}>
      <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
        <AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">
          {note.autore_nome?.split(' ').map(w => w[0]).join('') || '?'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-semibold text-slate-800">{note.autore_nome}</span>
          <span className="text-[10px] text-slate-400">{timeStr}</span>
          {note.audio_url && <Mic size={11} className="text-purple-500" />}
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{renderText(note.testo)}</p>
        {note.audio_url && (
          <audio controls className="mt-1.5 h-8 w-full max-w-[280px]" preload="none">
            <source src={`/api${note.audio_url}`} />
          </audio>
        )}
      </div>
    </div>
  );
};

/* ─── Status / Priority config ─── */
const statusConfig = {
  da_fare: { label: 'Da Fare', color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400', icon: Clock },
  in_corso: { label: 'In Corso', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertCircle },
  in_attesa: { label: 'In Attesa', color: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', icon: Clock },
  completato: { label: 'Completato', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle2 }
};

const priorityConfig = {
  urgente: { label: 'Urgente', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  alta: { label: 'Alta', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  media: { label: 'Media', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  bassa: { label: 'Bassa', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

const priorityOrder = { urgente: 0, alta: 1, media: 2, bassa: 3 };

/* ─── Main Component ─── */
export const Tasks = () => {
  const { isAdmin, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [sortBy, setSortBy] = useState('priorita');
  const [sortDir, setSortDir] = useState('asc');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [formData, setFormData] = useState({
    titolo: '', descrizione: '', priorita: 'media', assignee_id: '', scadenza: ''
  });

  useEffect(() => { fetchTasks(); fetchUsers(); }, []);

  const fetchTasks = async () => {
    try { const r = await api.get('/tasks'); setTasks(r.data); }
    catch { toast.error('Errore caricamento task'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch (e) { console.error('Errore caricamento utenti:', e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.assignee_id) { toast.error('Il campo "Assegna a" è obbligatorio: seleziona una persona'); return; }
    try {
      const payload = { ...formData };
      if (!payload.scadenza) delete payload.scadenza;
      await api.post('/tasks', payload);
      toast.success('Task creato!');
      setDialogOpen(false);
      setFormData({ titolo: '', descrizione: '', priorita: 'media', assignee_id: '', scadenza: '' });
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore creazione'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, stato: newStatus } : t));
    if (selectedTask?.id === taskId) setSelectedTask(prev => prev ? { ...prev, stato: newStatus } : prev);
    try { await api.patch(`/tasks/${taskId}`, { stato: newStatus }); toast.success('Stato aggiornato'); }
    catch { toast.error('Errore'); fetchTasks(); }
  };

  const handleAssigneeChange = async (taskId, newAssigneeId) => {
    const assignee = users.find(u => u.id === newAssigneeId);
    const assigneeName = assignee ? `${assignee.nome} ${assignee.cognome}` : null;
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assignee_id: newAssigneeId, assignee_nome: assigneeName } : t));
    if (selectedTask?.id === taskId) setSelectedTask(prev => prev ? { ...prev, assignee_id: newAssigneeId, assignee_nome: assigneeName } : prev);
    try { await api.patch(`/tasks/${taskId}`, { assignee_id: newAssigneeId }); toast.success('Riassegnato'); }
    catch { toast.error('Errore'); fetchTasks(); }
  };

  const handleAddNote = async (text, mentionIds) => {
    if (!selectedTask || !text.trim()) return;
    try {
      const res = await api.post(`/tasks/${selectedTask.id}/notes`, { testo: text, menzioni: mentionIds });
      setSelectedTask({ ...selectedTask, note: [...(selectedTask.note || []), res.data] });
      setNoteText('');
      toast.success('Nota aggiunta');
      fetchTasks();
    } catch { toast.error('Errore nota'); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Eliminare questo task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      if (selectedTask?.id === taskId) setSelectedTask(null);
      toast.success('Task eliminato');
    } catch { toast.error('Errore eliminazione'); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size > 0 && selectedTask) {
          setIsTranscribing(true);
          try {
            const fd = new FormData();
            fd.append('audio', audioBlob, 'voice_note.webm');
            const res = await api.post(`/tasks/${selectedTask.id}/voice-note`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
            setSelectedTask({ ...selectedTask, note: [...(selectedTask.note || []), res.data] });
            toast.success('Nota vocale aggiunta');
            fetchTasks();
          } catch { toast.error('Errore nota vocale'); }
          finally { setIsTranscribing(false); }
        }
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch { toast.error('Impossibile accedere al microfono'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); }
  };

  /* ─── Filtering & Sorting ─── */
  const filteredTasks = tasks.filter(t => {
    if (filter === 'mine' && t.assignee_id !== user?.id) return false;
    if (filter === 'unassigned' && t.assignee_id) return false;
    if (statusFilter !== 'all' && t.stato !== statusFilter) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!t.titolo?.toLowerCase().includes(q) && !t.descrizione?.toLowerCase().includes(q) && !t.assignee_nome?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'priorita') return (priorityOrder[a.priorita] - priorityOrder[b.priorita]) * dir;
    if (sortBy === 'scadenza') {
      if (!a.scadenza && !b.scadenza) return 0;
      if (!a.scadenza) return 1;
      if (!b.scadenza) return -1;
      return (new Date(a.scadenza) - new Date(b.scadenza)) * dir;
    }
    if (sortBy === 'stato') {
      const order = { da_fare: 0, in_corso: 1, in_attesa: 2, completato: 3 };
      return (order[a.stato] - order[b.stato]) * dir;
    }
    if (sortBy === 'titolo') return a.titolo.localeCompare(b.titolo) * dir;
    return 0;
  });

  const groupedTasks = {
    da_fare: filteredTasks.filter(t => t.stato === 'da_fare'),
    in_corso: filteredTasks.filter(t => t.stato === 'in_corso'),
    in_attesa: filteredTasks.filter(t => t.stato === 'in_attesa'),
    completato: filteredTasks.filter(t => t.stato === 'completato'),
  };

  const isOverdue = (t) => t.scadenza && new Date(t.scadenza) < new Date() && t.stato !== 'completato';

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown size={12} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-blue-600" /> : <ChevronDown size={12} className="text-blue-600" />;
  };

  /* ─── Stats ─── */
  const stats = [
    { label: 'Totali', value: tasks.length, color: 'text-slate-700', bg: 'bg-slate-50' },
    { label: 'Da Fare', value: tasks.filter(t => t.stato === 'da_fare').length, color: 'text-slate-600', bg: 'bg-slate-50' },
    { label: 'In Corso', value: tasks.filter(t => t.stato === 'in_corso').length, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'In Scadenza', value: tasks.filter(isOverdue).length, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Completati', value: tasks.filter(t => t.stato === 'completato').length, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Task</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filteredTasks.length} task visualizzati</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-task-button" className="bg-blue-600 hover:bg-blue-700">
              <Plus size={16} className="mr-2" /> Nuovo Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" aria-describedby={undefined}>
            <DialogHeader><DialogTitle>Crea Nuovo Task</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="titolo">Titolo</Label>
                <Input id="titolo" value={formData.titolo} onChange={(e) => setFormData({ ...formData, titolo: e.target.value })} required data-testid="task-title-input" />
              </div>
              <div>
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea id="descrizione" value={formData.descrizione} onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })} required rows={3} data-testid="task-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priorita</Label>
                  <Select value={formData.priorita} onValueChange={(v) => setFormData({ ...formData, priorita: v })}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bassa">Bassa</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assegna a *</Label>
                  <Select value={formData.assignee_id} onValueChange={(v) => setFormData({ ...formData, assignee_id: v })}>
                    <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="scadenza">Scadenza</Label>
                <Input id="scadenza" type="datetime-local" value={formData.scadenza} onChange={(e) => setFormData({ ...formData, scadenza: e.target.value })} data-testid="task-deadline-input" />
              </div>
              <Button type="submit" className="w-full" data-testid="task-submit-button">Crea Task</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {stats.map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-3.5 border border-slate-100`}>
            <p className="text-[11px] text-slate-500 font-medium mb-0.5">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca task..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="pl-9 h-9" data-testid="task-search" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5">
            {[
              { key: 'all', label: 'Tutti' },
              { key: 'mine', label: 'I Miei' },
              ...(isAdmin ? [{ key: 'unassigned', label: 'Liberi' }] : [])
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`task-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filter === f.key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
                {f.label}
              </button>
            ))}
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="status-filter">
              <Filter size={13} className="mr-1.5 text-slate-400" /><SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="da_fare">Da Fare</SelectItem>
              <SelectItem value="in_corso">In Corso</SelectItem>
              <SelectItem value="in_attesa">In Attesa</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
            </SelectContent>
          </Select>

          {/* View toggle */}
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 ml-auto sm:ml-0">
            <button onClick={() => setViewMode('table')} data-testid="view-table"
              className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-600'}`}>
              <List size={16} />
            </button>
            <button onClick={() => setViewMode('kanban')} data-testid="view-kanban"
              className={`p-1.5 rounded-md transition-all ${viewMode === 'kanban' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-600'}`}>
              <LayoutGrid size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ─── TABLE VIEW ─── */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tasks-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                    onClick={() => toggleSort('titolo')}>
                    <span className="flex items-center gap-1.5">Titolo <SortIcon col="titolo" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none w-[110px]"
                    onClick={() => toggleSort('priorita')}>
                    <span className="flex items-center gap-1.5">Priorita <SortIcon col="priorita" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none w-[140px]"
                    onClick={() => toggleSort('stato')}>
                    <span className="flex items-center gap-1.5">Stato <SortIcon col="stato" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider w-[150px]">Assegnato</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none w-[110px]"
                    onClick={() => toggleSort('scadenza')}>
                    <span className="flex items-center gap-1.5">Scadenza <SortIcon col="scadenza" /></span>
                  </th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider w-[60px]">Note</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider w-[60px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sortedTasks.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Nessun task trovato</td></tr>
                ) : sortedTasks.map(task => {
                  const pConf = priorityConfig[task.priorita] || priorityConfig.media;
                  const sConf = statusConfig[task.stato] || statusConfig.da_fare;
                  const overdue = isOverdue(task);
                  return (
                    <tr key={task.id} className={`group hover:bg-slate-50/80 transition-colors cursor-pointer ${overdue ? 'bg-red-50/30' : ''}`}
                      onClick={() => setSelectedTask(task)} data-testid={`task-row-${task.id}`}>
                      {/* Titolo */}
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <div className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${pConf.dot}`} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate max-w-[300px]">{task.titolo}</p>
                            {task.descrizione && <p className="text-xs text-slate-400 truncate max-w-[300px] mt-0.5">{task.descrizione}</p>}
                          </div>
                        </div>
                      </td>
                      {/* Priorita */}
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-[10px] font-medium ${pConf.color}`}>{pConf.label}</Badge>
                      </td>
                      {/* Stato */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={task.stato} onValueChange={(v) => handleStatusChange(task.id, v)}>
                          <SelectTrigger className={`h-7 text-[11px] font-medium border ${sConf.color}`} data-testid={`task-status-${task.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="da_fare">Da Fare</SelectItem>
                            <SelectItem value="in_corso">In Corso</SelectItem>
                            <SelectItem value="in_attesa">In Attesa</SelectItem>
                            <SelectItem value="completato">Completato</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      {/* Assegnato */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {isAdmin ? (
                          <Select value={task.assignee_id || 'none'} onValueChange={(v) => handleAssigneeChange(task.id, v === 'none' ? '' : v)}>
                            <SelectTrigger className={`h-7 text-[11px] ${!task.assignee_id ? 'border-amber-300 bg-amber-50 text-amber-700 font-semibold' : ''}`} data-testid={`task-assignee-${task.id}`}>
                              {task.assignee_id ? <SelectValue /> : <span>Da assegnare</span>}
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Non assegnato</SelectItem>
                              {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        ) : task.assignee_nome ? (
                          <div className="flex items-center gap-1.5">
                            <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">{task.assignee_nome?.split(' ').map(w => w[0]).join('')}</AvatarFallback></Avatar>
                            <span className="text-xs text-slate-600">{task.assignee_nome}</span>
                          </div>
                        ) : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`da-assegnare-${task.id}`}>Da assegnare</span>}
                      </td>
                      {/* Scadenza */}
                      <td className="px-4 py-3">
                        {task.scadenza ? (
                          <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                            <Clock size={11} />
                            {format(new Date(task.scadenza), 'dd/MM/yy', { locale: it })}
                          </span>
                        ) : <span className="text-xs text-slate-300">--</span>}
                      </td>
                      {/* Note */}
                      <td className="px-4 py-3 text-center">
                        {task.note?.length > 0 ? (
                          <span className="inline-flex items-center gap-0.5 text-slate-500">
                            <MessageSquare size={12} /><span className="text-[11px]">{task.note.length}</span>
                          </span>
                        ) : <span className="text-slate-300">--</span>}
                      </td>
                      {/* Delete */}
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleDeleteTask(task.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all rounded" data-testid={`delete-task-${task.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── KANBAN VIEW ─── */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {Object.entries(groupedTasks).map(([status, taskList]) => {
            const config = statusConfig[status];
            return (
              <div key={status} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                  <div className={`h-2.5 w-2.5 rounded-full ${config.dot}`} />
                  <h3 className="font-semibold text-slate-900 text-sm">{config.label}</h3>
                  <span className="ml-auto text-sm font-medium text-slate-400">{taskList.length}</span>
                </div>
                <div className="space-y-2.5">
                  {taskList.map(task => {
                    const pConf = priorityConfig[task.priorita] || priorityConfig.media;
                    return (
                      <div key={task.id} data-testid={`task-card-${task.id}`}
                        className={`p-3 rounded-lg border transition-all cursor-pointer ${
                          isOverdue(task) ? 'border-red-200 bg-red-50/50 hover:border-red-300' : 'border-slate-100 hover:border-blue-300 hover:shadow-sm'
                        }`} onClick={() => setSelectedTask(task)}>
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-slate-900 text-sm leading-tight flex-1 mr-2">{task.titolo}</h4>
                          <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${pConf.color}`}>{pConf.label}</Badge>
                        </div>
                        {task.descrizione && <p className="text-xs text-slate-500 line-clamp-2 mb-2">{task.descrizione}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5">
                            {task.assignee_nome ? (
                              <>
                                <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">{task.assignee_nome?.split(' ').map(w => w[0]).join('')}</AvatarFallback></Avatar>
                                <span className="text-[11px] text-slate-500">{task.assignee_nome.split(' ')[0]}</span>
                              </>
                            ) : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Da assegnare</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {task.note?.length > 0 && (
                              <span className="flex items-center gap-0.5 text-slate-400"><MessageSquare size={11} /><span className="text-[10px]">{task.note.length}</span></span>
                            )}
                            {task.scadenza && (
                              <span className={`text-[10px] flex items-center gap-0.5 ${isOverdue(task) ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                                <Clock size={10} />{format(new Date(task.scadenza), 'dd/MM', { locale: it })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {taskList.length === 0 && <div className="text-center py-8 text-slate-400 text-xs">Nessun task</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Task Detail Sheet ─── */}
      <Sheet open={!!selectedTask} onOpenChange={(open) => { if (!open) setSelectedTask(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="task-detail-sheet">
          {selectedTask && (
            <div className="space-y-5">
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <SheetTitle className="text-lg flex-1 pr-2">{selectedTask.titolo}</SheetTitle>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    onClick={() => handleDeleteTask(selectedTask.id)} data-testid="delete-task-btn">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </SheetHeader>

              <div className="flex gap-2 flex-wrap">
                <Badge className={statusConfig[selectedTask.stato]?.color}>{statusConfig[selectedTask.stato]?.label}</Badge>
                <Badge variant="outline" className={priorityConfig[selectedTask.priorita]?.color}>{priorityConfig[selectedTask.priorita]?.label}</Badge>
              </div>

              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Descrizione</p>
                <p className="text-sm text-slate-700 leading-relaxed">{selectedTask.descrizione || 'Nessuna descrizione'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {selectedTask.assignee_nome && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Assegnato a</p>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">{selectedTask.assignee_nome?.split(' ').map(w => w[0]).join('')}</AvatarFallback></Avatar>
                      <span className="text-sm text-slate-700">{selectedTask.assignee_nome}</span>
                    </div>
                  </div>
                )}
                {selectedTask.scadenza && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Scadenza</p>
                    <p className={`text-sm ${isOverdue(selectedTask) ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                      {format(new Date(selectedTask.scadenza), 'dd MMM yyyy HH:mm', { locale: it })}
                    </p>
                  </div>
                )}
              </div>

              {isAdmin && (
                <div>
                  <p className="text-[11px] font-semibold text-slate-400 uppercase mb-2">Assegna a</p>
                  <Select value={selectedTask.assignee_id || 'none'} onValueChange={(v) => handleAssigneeChange(selectedTask.id, v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="detail-assignee-select"><SelectValue placeholder="Non assegnato" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase mb-2">Cambia Stato</p>
                <Select value={selectedTask.stato} onValueChange={(v) => handleStatusChange(selectedTask.id, v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_fare">Da Fare</SelectItem>
                    <SelectItem value="in_corso">In Corso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Cronologia */}
              <div data-testid="task-cronologia">
                <div className="flex items-center gap-2 mb-2">
                  <History size={14} className="text-slate-400" />
                  <p className="text-[11px] font-semibold text-slate-400 uppercase">Cronologia</p>
                </div>
                <div className="space-y-1 text-xs text-slate-500">
                  <p>Creato da <span className="font-semibold text-slate-700">{selectedTask.creatore_nome || '—'}</span>{selectedTask.created_at ? ` il ${format(new Date(selectedTask.created_at), 'dd MMM yyyy HH:mm', { locale: it })}` : ''}</p>
                  {selectedTask.updated_by_nome && selectedTask.updated_at !== selectedTask.created_at && (
                    <p>Ultima modifica di <span className="font-semibold text-slate-700">{selectedTask.updated_by_nome}</span>{selectedTask.updated_at ? ` il ${format(new Date(selectedTask.updated_at), 'dd MMM yyyy HH:mm', { locale: it })}` : ''}</p>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={16} className="text-slate-500" />
                  <p className="text-sm font-semibold text-slate-900">Note e Commenti ({selectedTask.note?.length || 0})</p>
                </div>
                <div className="space-y-0 mb-4 max-h-[300px] overflow-y-auto">
                  {(!selectedTask.note || selectedTask.note.length === 0) ? (
                    <p className="text-xs text-slate-400 text-center py-4">Nessuna nota. Usa @ per menzionare i colleghi.</p>
                  ) : (
                    selectedTask.note.map((note, idx) => {
                      if (typeof note === 'string') return <div key={idx} className="py-2 text-sm text-slate-600 border-b border-slate-100 last:border-0">{note}</div>;
                      return <NoteItem key={note.id || idx} note={note} />;
                    })
                  )}
                </div>
                <div className="space-y-2">
                  <MentionInput value={noteText} onChange={setNoteText} onSubmit={handleAddNote} users={users} placeholder="Aggiungi nota... usa @ per menzionare" />
                  <div className="flex items-center gap-2">
                    {isTranscribing ? (
                      <div className="flex items-center gap-2 text-xs text-purple-600"><Loader2 size={14} className="animate-spin" /><span>Trascrizione...</span></div>
                    ) : (
                      <Button type="button" size="sm" variant={isRecording ? "destructive" : "outline"}
                        className={`h-8 text-xs gap-1.5 ${isRecording ? '' : 'text-purple-600 border-purple-200 hover:bg-purple-50'}`}
                        onClick={isRecording ? stopRecording : startRecording} data-testid="voice-note-btn">
                        {isRecording ? <><MicOff size={13} /> Stop</> : <><Mic size={13} /> Nota Vocale</>}
                      </Button>
                    )}
                    {isRecording && <span className="flex items-center gap-1 text-xs text-red-500"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Registrazione...</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
